import type { ItemKind, Shape } from "./types";

export interface CatalogEntry {
  id: string;
  label: string;
  kind: ItemKind;
  shape: Shape;
  w: number; // cm
  h: number;
  color?: string;
  seats?: number;
  /** Optional furniture cluster spawned alongside the room when added from
   *  the library. Positions are relative to the room's top-left (cm). */
  defaultCluster?: ClusterChild[];
}

export interface ClusterChild {
  type: string; // catalog id reference (for label/type), free-form
  label: string;
  kind: ItemKind;
  shape: Shape;
  x: number;
  y: number;
  w: number;
  h: number;
  rotation?: number;
  color?: string;
  seats?: number;
  flipArc?: boolean; // door-only: arc on opposite side of the door leaf
}


export const CATEGORIES: { kind: ItemKind; label: string }[] = [
  { kind: "room", label: "Rooms" },
  { kind: "furniture", label: "Furniture" },
  { kind: "storage", label: "Storage" },
  { kind: "fixture", label: "Fixtures" },
  { kind: "architectural", label: "Architectural" },
  { kind: "equipment", label: "Equipment" },
];

export const CATALOG: CatalogEntry[] = [
  // Rooms
  {
    id: "room-director",
    label: "Director Cabin (9×8 ft)",
    kind: "room",
    shape: "rect",
    w: 274,
    h: 244,
    color: "oklch(0.92 0.05 250 / 0.55)",
    // Cluster: executive desk centered, director chair behind, 2 guest chairs
    // in front, filing cabinet tucked into the back-right corner.
    defaultCluster: [
      // L-shaped director desk in the top-left corner of the cabin.
      // Overall L bounding box = 183cm wide × 152cm tall, matching the
      // reference 9×8 ft private cabin layout.
      //  - Vertical leg (storage side): 61 × 152 against the left wall
      //  - Horizontal leg (work surface): 183 × 76 across the top
      // Storage column (vertical leg of the L)
      { type: "locker", label: "Storage Column", kind: "storage", shape: "shelf", x: 0, y: 0, w: 61, h: 152 },
      // Work surface (horizontal leg of the L) — overlaps the storage at the corner
      { type: "desk-exec", label: "Director Desk", kind: "furniture", shape: "desk", x: 0, y: 0, w: 183, h: 76 },
      // Director chair sits in the L pocket, facing the guests (south)
      { type: "chair", label: "Director Chair", kind: "furniture", shape: "chair", x: 97, y: 89, w: 50, h: 50, rotation: 0 },
      // Two guest chairs in front of the desk, facing the director (north)
      { type: "chair", label: "Guest Chair", kind: "furniture", shape: "chair", x: 55, y: 180, w: 50, h: 50, rotation: 180 },
      { type: "chair", label: "Guest Chair", kind: "furniture", shape: "chair", x: 120, y: 180, w: 50, h: 50, rotation: 180 },
      // Inward-swing entry door on the right wall.
      { type: "door", label: "Entry Door", kind: "architectural", shape: "door", x: 234, y: 160, w: 80, h: 10, rotation: 90, flipArc: false },
    ],
  },
  { id: "room-cabin-large", label: "Large Cabin (16×9 ft)", kind: "room", shape: "rect", w: 488, h: 274, color: "oklch(0.92 0.05 270 / 0.55)" },
  {
    id: "room-team-cabin",
    label: "Team Cabin (9×8 ft) — L-Arc",
    kind: "room",
    shape: "rect",
    w: 274,
    h: 244,
    color: "oklch(0.92 0.05 200 / 0.55)",
    // Team cabin with an L-shaped table (curved inner corner) tucked against
    // the top + left walls. 6 chairs face the table — 3 along the top arm,
    // 3 along the left arm. Inward entry door on the right wall.
    defaultCluster: [
      // L-arc table footprint: top arm 274×60, left arm 60×244, with a
      // concave quarter-arc at the inner corner (rendered by the shape).
      { type: "l-arc-table", label: "L-Arc Team Table", kind: "furniture", shape: "l-arc-table", x: 0, y: 0, w: 274, h: 244 },
      // Top-arm chairs (face north — backs to the south, looking at the table/wall)
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 95, y: 70, w: 45, h: 45, rotation: 180 },
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 160, y: 70, w: 45, h: 45, rotation: 180 },
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 220, y: 70, w: 45, h: 45, rotation: 180 },
      // Left-arm chairs (face west — looking at the table)
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 70, y: 95, w: 45, h: 45, rotation: 90 },
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 70, y: 155, w: 45, h: 45, rotation: 90 },
      { type: "chair", label: "Team Chair", kind: "furniture", shape: "chair", x: 70, y: 215, w: 45, h: 45, rotation: 90 },
      // Inward-swing entry door on the right wall
      { type: "door", label: "Entry Door", kind: "architectural", shape: "door", x: 234, y: 160, w: 80, h: 10, rotation: 90, flipArc: false },
    ],
  },
  { id: "room-cabin-mid", label: "Mid-Size Cabin (10×12 ft)", kind: "room", shape: "rect", w: 305, h: 366, color: "oklch(0.92 0.05 230 / 0.55)" },
  { id: "room-conference", label: "Conference Room (16×12 ft)", kind: "room", shape: "rect", w: 488, h: 366, color: "oklch(0.92 0.06 180 / 0.55)" },
  { id: "room-workstation", label: "Workstation Area", kind: "room", shape: "rect", w: 800, h: 600, color: "oklch(0.94 0.05 140 / 0.55)" },
  { id: "room-reception", label: "Reception (6×4 ft)", kind: "room", shape: "rect", w: 183, h: 122, color: "oklch(0.92 0.06 50 / 0.55)" },
  { id: "room-washroom", label: "Washroom", kind: "room", shape: "rect", w: 300, h: 250, color: "oklch(0.93 0.04 220 / 0.55)" },
  {
    id: "room-washroom-male",
    label: "Washroom · Male (12×8 ft)",
    kind: "room",
    shape: "rect",
    w: 366,
    h: 244,
    color: "oklch(0.93 0.05 230 / 0.55)",
    defaultCluster: [
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 0, y: 150, w: 184, h: 4 },
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 90, y: 150, w: 4, h: 94 },
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 180, y: 150, w: 4, h: 94 },
      { type: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", x: 22, y: 175, w: 45, h: 65, rotation: 0 },
      { type: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", x: 112, y: 175, w: 45, h: 65, rotation: 0 },
      { type: "urinal", label: "Urinal", kind: "fixture", shape: "urinal", x: 30, y: 0, w: 40, h: 30 },
      { type: "urinal", label: "Urinal", kind: "fixture", shape: "urinal", x: 110, y: 0, w: 40, h: 30 },
      { type: "urinal", label: "Urinal", kind: "fixture", shape: "urinal", x: 190, y: 0, w: 40, h: 30 },
      { type: "sink", label: "Sink", kind: "fixture", shape: "sink", x: 306, y: 30, w: 60, h: 45, rotation: 270 },
      { type: "sink", label: "Sink", kind: "fixture", shape: "sink", x: 306, y: 90, w: 60, h: 45, rotation: 270 },
      { type: "door", label: "Entry Door", kind: "architectural", shape: "door", x: 326, y: 170, w: 80, h: 10, rotation: 90, flipArc: false },
    ],
  },
  {
    id: "room-washroom-female",
    label: "Washroom · Female (10×8 ft)",
    kind: "room",
    shape: "rect",
    w: 305,
    h: 244,
    color: "oklch(0.93 0.05 340 / 0.55)",
    defaultCluster: [
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 0, y: 90, w: 305, h: 4 },
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 100, y: 0, w: 4, h: 90 },
      { type: "counter", label: "Stall Partition", kind: "storage", shape: "rect", x: 200, y: 0, w: 4, h: 90 },
      { type: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", x: 27, y: 18, w: 45, h: 65, rotation: 0 },
      { type: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", x: 130, y: 18, w: 45, h: 65, rotation: 0 },
      { type: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", x: 230, y: 18, w: 45, h: 65, rotation: 0 },
      { type: "sink", label: "Sink", kind: "fixture", shape: "sink", x: 60, y: 200, w: 60, h: 45, rotation: 180 },
      { type: "sink", label: "Sink", kind: "fixture", shape: "sink", x: 180, y: 200, w: 60, h: 45, rotation: 180 },
      { type: "door", label: "Entry Door", kind: "architectural", shape: "door", x: 265, y: 170, w: 80, h: 10, rotation: 90, flipArc: false },
    ],
  },
  { id: "room-cafeteria", label: "Cafeteria (12×8 ft)", kind: "room", shape: "rect", w: 366, h: 244, color: "oklch(0.94 0.06 80 / 0.55)" },
  // Phase 54 Plan 05 (SPACE-UX-UNIT-KIND-08): 4 amenity room entries the
  // roomResourceType() mapper already had substring branches for (Bay/Store_Room)
  // or now gains branches for (Pantry/Washbasin) — see projection.ts. The `id`
  // prefixes below MUST each match exactly one roomResourceType() branch.
  { id: "room-bay", label: "Bay (8×8 ft)", kind: "room", shape: "rect", w: 244, h: 244, color: "oklch(0.94 0.05 95 / 0.55)" },
  { id: "room-pantry", label: "Pantry (8×6 ft)", kind: "room", shape: "rect", w: 244, h: 183, color: "oklch(0.94 0.06 70 / 0.55)" },
  { id: "room-store", label: "Store Room (6×8 ft)", kind: "room", shape: "rect", w: 183, h: 244, color: "oklch(0.92 0.04 40 / 0.55)" },
  { id: "room-washbasin", label: "Washbasin (4×4 ft)", kind: "room", shape: "rect", w: 122, h: 122, color: "oklch(0.93 0.05 210 / 0.55)" },
  { id: "room-podcast", label: "Podcast Room (10×12 ft)", kind: "room", shape: "rect", w: 305, h: 366, color: "oklch(0.92 0.06 310 / 0.55)" },
  { id: "room-phonebooth", label: "Phone Booth", kind: "room", shape: "rect", w: 150, h: 150, color: "oklch(0.93 0.05 290 / 0.55)" },
  { id: "room-lounge", label: "Lounge / Waiting (8×8 ft)", kind: "room", shape: "rect", w: 244, h: 244, color: "oklch(0.93 0.05 20 / 0.55)" },
  { id: "room-open", label: "Open Area", kind: "room", shape: "rect", w: 600, h: 500, color: "oklch(0.95 0.02 250 / 0.55)" },

  // Furniture
  { id: "desk", label: "Desk", kind: "furniture", shape: "desk", w: 140, h: 70 },
  { id: "desk-exec", label: "Executive Desk", kind: "furniture", shape: "desk", w: 200, h: 100 },
  { id: "conf-4", label: "Conference Table (4)", kind: "furniture", shape: "conference", w: 160, h: 90, seats: 4 },
  { id: "conf-6", label: "Conference Table (6)", kind: "furniture", shape: "conference", w: 240, h: 100, seats: 6 },
  { id: "conf-8", label: "Conference Table (8)", kind: "furniture", shape: "conference", w: 320, h: 110, seats: 8 },
  { id: "conf-12", label: "Conference Table (12)", kind: "furniture", shape: "conference", w: 480, h: 120, seats: 12 },
  // Large boardroom-style conference room table (table only; chairs added
  // separately) with rounded ends and cable spine.
  { id: "conf-room-10", label: "Conference Room Table (10) — table only", kind: "furniture", shape: "conference-room", w: 400, h: 140, seats: 10 },
  { id: "conf-room-14", label: "Conference Room Table (14) — table only", kind: "furniture", shape: "conference-room", w: 540, h: 140, seats: 14 },
  // Workstation — face-to-face (back-to-back desks, people face each other across a center spine).
  { id: "workstation-ftf-4", label: "Workstation · Face-to-Face (4)", kind: "furniture", shape: "workstation", w: 220, h: 122, seats: 4 },
  { id: "workstation-ftf-8", label: "Workstation · Face-to-Face (8)", kind: "furniture", shape: "workstation", w: 220, h: 244, seats: 8 },
  { id: "workstation-ftf-12", label: "Workstation · Face-to-Face (12)", kind: "furniture", shape: "workstation", w: 220, h: 366, seats: 12 },
  { id: "workstation-ftf-16", label: "Workstation · Face-to-Face (16)", kind: "furniture", shape: "workstation", w: 220, h: 488, seats: 16 },
  // Workstation — 1-wall facing (single row of desks along one wall, chairs on the open side).
  { id: "workstation-wall1-3", label: "Workstation · 1-Wall (3)", kind: "furniture", shape: "workstation-linear", w: 110, h: 183, seats: 3 },
  { id: "workstation-wall1-5", label: "Workstation · 1-Wall (5)", kind: "furniture", shape: "workstation-linear", w: 110, h: 305, seats: 5 },
  { id: "workstation-wall1-8", label: "Workstation · 1-Wall (8)", kind: "furniture", shape: "workstation-linear", w: 110, h: 488, seats: 8 },
  // Workstation — 2-wall facing (L-shaped corner desk; corner is unoccupiable, seats 1).
  { id: "workstation-wall2", label: "Workstation · 2-Wall Corner (1)", kind: "furniture", shape: "workstation-corner", w: 180, h: 180, seats: 1 },
  { id: "workstation-wall2-lg", label: "Workstation · 2-Wall Corner L (1)", kind: "furniture", shape: "workstation-corner", w: 220, h: 220, seats: 1 },
  // Workstation — L-shaped wall-facing (6 seats along two walls, corner unoccupiable).
  { id: "workstation-l-6", label: "Workstation · L-Shape 6-Wall", kind: "furniture", shape: "workstation-l", w: 240, h: 240, seats: 6 },
  // Workstation TABLE only (no chairs) — default 5 columns × 2 rows = 10 seats,
  // back-to-back layout. Each seat ~120cm wide × 60cm deep.
  { id: "workstation-table-10", label: "Workstation Table (5×2 = 10)", kind: "furniture", shape: "workstation-table", w: 600, h: 120, seats: 10 },
  // L-shaped table with curved outer ends — useful for manager desks or
  // informal discussion nooks where the inner corner is rounded too.
  { id: "l-curv-table", label: "L Curv Table", kind: "furniture", shape: "l-curv-table", w: 180, h: 160 },
  { id: "chair", label: "Office Chair", kind: "furniture", shape: "chair", w: 50, h: 50 },
  { id: "chair-lounge", label: "Lounge Chair", kind: "furniture", shape: "chair", w: 80, h: 80 },
  { id: "sofa-2", label: "Sofa (2-seat)", kind: "furniture", shape: "sofa", w: 160, h: 85 },
  { id: "sofa-3", label: "Sofa (3-seat)", kind: "furniture", shape: "sofa", w: 220, h: 85 },
  { id: "coffee-table", label: "Coffee Table", kind: "furniture", shape: "rect", w: 100, h: 60 },
  { id: "stool", label: "Stool", kind: "furniture", shape: "circle", w: 40, h: 40 },

  // Storage
  { id: "bookshelf", label: "Bookshelf", kind: "storage", shape: "shelf", w: 120, h: 35 },
  { id: "filing", label: "Filing Cabinet", kind: "storage", shape: "shelf", w: 80, h: 45 },
  { id: "locker", label: "Locker Bank", kind: "storage", shape: "shelf", w: 180, h: 45 },
  { id: "fridge", label: "Refrigerator", kind: "storage", shape: "fridge", w: 70, h: 70 },
  { id: "fridge-micro", label: "Fridge + Microwave", kind: "storage", shape: "fridge", w: 70, h: 70 },
  { id: "microwave", label: "Microwave", kind: "storage", shape: "microwave", w: 50, h: 35 },
  { id: "oven", label: "Oven", kind: "storage", shape: "oven", w: 60, h: 60 },
  { id: "ro-dispenser", label: "RO Water Dispenser", kind: "storage", shape: "ro-dispenser", w: 40, h: 40 },
  { id: "counter", label: "Kitchen Counter", kind: "storage", shape: "rect", w: 240, h: 60 },

  // Fixtures
  { id: "sink", label: "Sink", kind: "fixture", shape: "sink", w: 60, h: 45 },
  { id: "toilet", label: "Toilet", kind: "fixture", shape: "toilet", w: 45, h: 65 },
  { id: "urinal", label: "Urinal", kind: "fixture", shape: "urinal", w: 45, h: 60 },

  // Architectural
  { id: "pillar-square", label: "Pillar (square)", kind: "architectural", shape: "pillar-square", w: 50, h: 50 },
  { id: "pillar-round", label: "Pillar (round)", kind: "architectural", shape: "pillar-round", w: 50, h: 50 },
  { id: "door", label: "Door", kind: "architectural", shape: "door", w: 90, h: 10 },
  { id: "window", label: "Window", kind: "architectural", shape: "rect", w: 120, h: 10, color: "oklch(0.85 0.08 220)" },
  { id: "entry", label: "Entry", kind: "architectural", shape: "entry", w: 120, h: 30 },
  { id: "lift", label: "Lift", kind: "architectural", shape: "lift", w: 180, h: 180 },
  { id: "stairs", label: "Staircase", kind: "architectural", shape: "stairs", w: 260, h: 120 },

  // Equipment
  { id: "whiteboard", label: "Whiteboard", kind: "equipment", shape: "rect", w: 180, h: 10, color: "oklch(0.97 0.005 250)" },
  { id: "tv", label: "TV / Display", kind: "equipment", shape: "tv", w: 140, h: 12 },
  // Ceiling-mounted projector — small square with lens. ~30×30 cm footprint.
  { id: "projector", label: "Projector", kind: "equipment", shape: "projector", w: 35, h: 30 },
  { id: "mic", label: "Podcast Mic", kind: "equipment", shape: "mic", w: 30, h: 30 },
  { id: "acoustic", label: "Acoustic Panel", kind: "equipment", shape: "rect", w: 60, h: 8, color: "oklch(0.6 0.05 30)" },
  { id: "plant", label: "Plant", kind: "equipment", shape: "plant", w: 45, h: 45 },
];

export const CATALOG_BY_ID = Object.fromEntries(CATALOG.map((c) => [c.id, c]));